import {useState} from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import ValidationRules from './ValidationRules'
import Modal from '../../components/common/Modal/ModalComponent'
import {StyledCancelButton} from '../AdminPanelStyling'
import SnackbarComponent from '../common/Snackbar/SnackbarComponent'
import SnackbarMessages from '../common/Snackbar/SnackbarMessages.json'
import {VALIDATION_PATTERNS, VALIDATION_MESSAGES} from '../common/Helpers/formValidation'
import InlineErrors from '../common/Errors/InlineErrors.json'
import {specialCharactersForDescription} from '../ProjectDetails/ProjectDescription/regexData'
import TextAreaWithCounter from '../common/TextAreaWithCounter/TextAreaWithCounter'
import {useCreateProjectDetails} from '../api/createProject'
import {
  StyledConfirmationButton,
  StyledInfoIcon,
  StyledProjectConfirmationCancelButton,
  StyledErrorMessage
} from './AddClientModalStyle'
import TextFieldWithCounter from '../common/TextFieldWithCounter/TextFieldWithCounter'

const PROJECT_NAME_MAX_LENGTH = 50
const PROJECT_DESCRIPTION_MAX_LENGTH = 100
const INITIAL_FORM_VALUES = {
  projectName: '',
  projectDescription: ''
}

const AddProjectModel = ({clientId, onClose, open, onSave, existingProjects}) => {
  const [formValues, setFormValues] = useState(INITIAL_FORM_VALUES)
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false)
  const [saving, setSaving] = useState(false)
  const [snackbar, setSnackbar] = useState({show: false, message: '', isError: false})

  const [createProject] = useCreateProjectDetails()

  const trimmedProjectName = formValues.projectName.trim()
  const trimmedProjectDescription = formValues.projectDescription.trim()
  const isDuplicateProjectName =
    trimmedProjectName.length > 0 &&
    Array.isArray(existingProjects) &&
    existingProjects.some((projectName) => projectName?.trim() === trimmedProjectName)
  const isProjectNameOverLimit = formValues.projectName.length > PROJECT_NAME_MAX_LENGTH

  const isProjectNameValid =
    trimmedProjectName.length > 0 &&
    VALIDATION_PATTERNS.ENTITY_NAME.test(trimmedProjectName) &&
    !isDuplicateProjectName &&
    !isProjectNameOverLimit

  let projectNameErrorMessage = ''
  if (isProjectNameOverLimit) {
    projectNameErrorMessage = VALIDATION_MESSAGES.MAX_LENGTH(PROJECT_NAME_MAX_LENGTH)
  } else if (formValues.projectName.length > 0 && trimmedProjectName.length === 0) {
    projectNameErrorMessage = VALIDATION_MESSAGES.REQUIRED('Project name')
  } else if (isDuplicateProjectName) {
    projectNameErrorMessage = 'Name already exists'
  }

  const hasDescriptionSpecialCharacters =
    trimmedProjectDescription.length > 0 && Boolean(trimmedProjectDescription.match(specialCharactersForDescription))

  const isProjectDescriptionOverLimit = formValues.projectDescription.length > PROJECT_DESCRIPTION_MAX_LENGTH

  const isProjectDescriptionValid = !hasDescriptionSpecialCharacters && !isProjectDescriptionOverLimit

  let projectDescriptionErrorMessage = ''
  if (hasDescriptionSpecialCharacters) {
    projectDescriptionErrorMessage = InlineErrors.speicalCharactersInDescriptionError
  }

  const isSaveEnabled = isProjectNameValid && isProjectDescriptionValid
  const hasUnsavedChanges =
    formValues.projectName !== INITIAL_FORM_VALUES.projectName ||
    formValues.projectDescription !== INITIAL_FORM_VALUES.projectDescription

  const handleInputChange = (e) => {
    const {name, value} = e.target
    setFormValues((prev) => ({...prev, [name]: value}))
  }

  const closeConfirmationDialog = () => {
    setShowConfirmationDialog(false)
  }

  const handleConfirmLeave = () => {
    setShowConfirmationDialog(false)
    onClose()
  }

  const handleCancel = () => {
    if (!hasUnsavedChanges) {
      onClose()
      return
    }

    setShowConfirmationDialog(true)
  }

  const handleSaveProject = async () => {
    if (!isSaveEnabled || saving) return

    setSaving(true)
    try {
      const result = await createProject({
        variables: {
          clientId: clientId.toString(),
          groupName: formValues.projectName.trim(),
          groupType: '3',
          description: formValues.projectDescription.trim() || '',
          filters: [],
          jurisdiction: [],
          dataAggregation: [],
          trendAnalysisFlag: [0],
          chatbotFlag: [0],
          chatbotExportFlag: [0],
          chatbotCopyFlag: [0],
          exportFlag: [0],
          benefits: [],
          workforces: [],
          usecase: [],
          personaFlag: [0],
          pointOfInterest: []
        }
      })

      if (!result.data?.creategroup?.userGroup?.group_id) {
        throw new Error(SnackbarMessages.createGroupError?.[0] || 'Failed to create project')
      }

      setSnackbar({
        show: true,
        message: SnackbarMessages.projectSaved || 'Project created successfully',
        isError: false
      })
      setFormValues(INITIAL_FORM_VALUES)
      await onSave()
      setTimeout(() => {
        onClose()
      }, 500)
    } catch {
      setSnackbar({
        show: true,
        message:
          SnackbarMessages.projectSaveError || 'Something went wrong while creating the project. Please try again.',
        isError: true
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Modal
        onClose={handleCancel}
        title='Add New Project'
        open={open}
        disableBackdropClick
        aria={{
          labelledBy: 'add-project-modal-title'
        }}
        maxWidth='md'
      >
        <Box display='flex' flexDirection='column' gap={2} padding={2}>
          <Box display='flex' flexDirection='column' gap={1}>
            <TextFieldWithCounter
              label='Project Name'
              name='projectName'
              value={formValues.projectName}
              onChange={handleInputChange}
              maxLength={PROJECT_NAME_MAX_LENGTH}
              enforceMaxLength={false}
              fullWidth
              size='medium'
              error={Boolean(projectNameErrorMessage)}
              helperText={projectNameErrorMessage}
            />
            <ValidationRules refuseNumbers refuseSpecialCharacters />
          </Box>
          <TextAreaWithCounter
            label='Project Description'
            name='projectDescription'
            value={formValues.projectDescription}
            onChange={handleInputChange}
            maxLength={PROJECT_DESCRIPTION_MAX_LENGTH}
            error={hasDescriptionSpecialCharacters || isProjectDescriptionOverLimit}
            fullWidth
            size='medium'
            minRows={4}
          />
          {!!projectDescriptionErrorMessage && (
            <StyledErrorMessage>{projectDescriptionErrorMessage}</StyledErrorMessage>
          )}
          <Box display='flex' justifyContent='flex-end' gap={1} mt={3}>
            <StyledCancelButton onClick={handleCancel}>Cancel</StyledCancelButton>
            <Button variant='contained' disabled={!isSaveEnabled || saving} onClick={handleSaveProject}>
              {saving ? <CircularProgress size={20} color='inherit' /> : 'Proceed'}
            </Button>
          </Box>
        </Box>
      </Modal>
      <Modal
        open={showConfirmationDialog}
        onClose={closeConfirmationDialog}
        aria={{labelledBy: 'add-project-cancel-confirmation-modal'}}
        maxWidth='xs'
      >
        <Box display='flex' flexDirection='column' alignItems='center' gap={2} p={2}>
          <StyledInfoIcon />
          <Typography variant='h6' fontWeight='bold' textAlign='center'>
            Are you sure you want to leave?
          </Typography>
          <Typography variant='body1' textAlign='center'>
            Leaving will cancel the creation of this Project and you’ll lose any changes unless you Proceed to set
            project permissions and click “Save”.
          </Typography>
          <Box display='flex' flexDirection='column' alignItems='center' gap={1} width='80%' mt={2}>
            <StyledConfirmationButton onClick={handleConfirmLeave} variant='contained'>
              Yes, cancel this Project
            </StyledConfirmationButton>
            <StyledProjectConfirmationCancelButton onClick={closeConfirmationDialog}>
              No, I would like to Proceed for Project Creation
            </StyledProjectConfirmationCancelButton>
          </Box>
        </Box>
      </Modal>
      <SnackbarComponent
        showNotification={snackbar.show}
        setShowNotification={(show) => setSnackbar((prev) => ({...prev, show}))}
        message={snackbar.message}
        error={snackbar.isError}
      />
    </>
  )
}

AddProjectModel.propTypes = {
  clientId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  onSave: PropTypes.func,
  existingProjects: PropTypes.array
}

export default AddProjectModel
